import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/api';

// Settings → Data health (super admin only). Reworked from the old
// AdminHealth page: the headline numbers become the design's stat cards
// and problem jobs surface in a "Needs attention" table; the remaining
// diagnostics (crons, freshness, funnel, sentiment, credit spend,
// integrations) keep their data but move onto Organic cards.

const STROKE = 2.75;

function formatDate(value) {
  if (!value) return 'never';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function ageLabel(days) {
  if (days == null) return 'never';
  if (days < 1) return 'today';
  if (days < 2) return '1 day ago';
  return `${Math.floor(days)} days ago`;
}

function StatusTag({ ok, warnLabel = 'Overdue', okLabel = 'OK' }) {
  return ok ? (
    <span className="tag tag-accent-2"><CheckCircle2 size={11} strokeWidth={STROKE} style={{ marginRight: 4 }} /> {okLabel}</span>
  ) : (
    <span className="tag tag-accent"><AlertTriangle size={11} strokeWidth={STROKE} style={{ marginRight: 4 }} /> {warnLabel}</span>
  );
}

function StatCard({ kicker, value, note, color }) {
  return (
    <div className="card" style={{ gap: 2, padding: 'var(--space-4)' }}>
      <span className="card-kicker">{kicker}</span>
      <span className="o-stat-value-lg" style={color ? { color } : undefined}>{value}</span>
      {note && <span className="o-stat-note">{note}</span>}
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <div>
        <h4 className="o-card-head-title">{title}</h4>
        {subtitle && <p className="o-card-head-sub">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function HealthTab() {
  const { isSuperAdmin } = useAuth();
  const isSuper = isSuperAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getAdminHealth());
    } catch (e) {
      setError(e.message || 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuper) load(); }, [load, isSuper]);

  // Super admin only — the tab is never offered to org admins, and the
  // backend rejects the calls anyway; this is defence in depth.
  if (!isSuper) return null;

  if (loading && !data) {
    return (
      <div className="card" style={{ padding: 'var(--space-4)', flexDirection: 'row', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
        <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading health data…
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--color-accent-700)' }}>
        Error: {error}
      </div>
    );
  }
  if (!data) return null;

  const crons = data.crons;
  const jobs = data.jobs;
  const freshness = data.freshness;
  const funnel = data.funnel;
  const sentiment = data.sentiment;
  const credits = data.credits;
  const integrations = data.integrations || {};

  const totals = freshness?.totals || {};
  const staleCount = Math.max(0, (totals.brands || 0) - (totals.synced_this_week || 0));
  const credits30d = (credits?.last_30d_by_tenant || []).reduce((sum, t) => sum + (t.credits || 0), 0);

  const attention = [
    ...(jobs?.stuck || []).map((j) => ({ ...j, _state: 'Stuck', _problem: 'Job has not progressed' })),
    ...(jobs?.recent_failures || []).map((j) => ({ ...j, _state: 'Failed', _problem: j.error || 'Job failed' })),
    ...(crons?.weekly_sync?.recent_failures || []).map((f, i) => ({
      kind: 'weekly sync', id: `sync-${i}`, org_id: f.org_id,
      updated_at: f.started_at, _state: 'Failed', _problem: f.error || 'Sync run failed',
    })),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div>
          <h4 style={{ margin: 0 }}>Data health</h4>
          <p className="o-card-head-sub">
            Crons, background jobs, data freshness, discovery output and credit spend.
            {data.generated_at && <> Snapshot: {formatDate(data.generated_at)}.</>}
          </p>
        </div>
        <button type="button" className="btn btn-secondary" style={{ fontSize: 13 }} onClick={load} disabled={loading}>
          <RefreshCw size={14} strokeWidth={STROKE} /> Refresh
        </button>
      </div>

      {error && (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-accent-700)' }}>Error: {error}</p>
      )}

      <div className="o-stat-grid">
        <StatCard
          kicker="Last sync"
          value={ageLabel(crons?.weekly_sync?.age_days)}
          note={crons?.weekly_sync?.last_status ? `last outcome: ${crons.weekly_sync.last_status}` : null}
        />
        <StatCard
          kicker="Fresh metrics"
          value={totals.synced_this_week ?? '—'}
          note={`of ${totals.brands ?? '—'} brands`}
          color="var(--color-accent-2-700)"
        />
        <StatCard
          kicker="Not synced this week"
          value={staleCount}
          note={`${totals.enrichment_failed ?? 0} enrichment failure(s)`}
          color={staleCount > 0 ? 'var(--color-accent-700)' : undefined}
        />
        <StatCard
          kicker="Research credits"
          value={credits30d.toLocaleString()}
          note="used in the last 30 days"
        />
      </div>

      <SectionCard title="Needs attention">
        {attention.length === 0 ? (
          <p style={{ margin: 0, fontSize: 14, opacity: 0.65 }}>
            No stuck jobs, failed jobs or failed sync runs.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Problem</th>
                <th>Updated</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {attention.map((j) => (
                <tr key={`${j.kind}-${j.id}`}>
                  <td style={{ fontWeight: 600 }}>{j.kind} #{j.id}{j.org_id ? ` · org ${j.org_id}` : ''}</td>
                  <td>{j._problem}</td>
                  <td>{formatDate(j.updated_at)}</td>
                  <td>
                    <span className="tag tag-accent">
                      {j._state === 'Stuck'
                        ? <AlertTriangle size={11} strokeWidth={STROKE} style={{ marginRight: 4 }} />
                        : <XCircle size={11} strokeWidth={STROKE} style={{ marginRight: 4 }} />}
                      {j._state}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title="Scheduled crons">
        <table className="table">
          <thead>
            <tr>
              <th>Cron</th>
              <th>Last run</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Weekly social sync</td>
              <td title={formatDate(crons.weekly_sync.last_run_at)}>{ageLabel(crons.weekly_sync.age_days)}</td>
              <td><StatusTag ok={!crons.weekly_sync.overdue} /></td>
              <td style={{ fontSize: 13, opacity: 0.8 }}>
                {crons.weekly_sync.last_status && `last outcome: ${crons.weekly_sync.last_status}`}
                {crons.weekly_sync.last_error && ` · ${crons.weekly_sync.last_error}`}
                {crons.weekly_sync.recent_failures.length > 0 &&
                  ` · ${crons.weekly_sync.recent_failures.length} failed run(s) in 14d`}
              </td>
            </tr>
            {crons.discovery.map((d) => (
              <tr key={d.region}>
                <td>Discovery — {d.label}</td>
                <td title={formatDate(d.last_billed_activity_at)}>
                  {d.subscribed_tenants === 0 ? '—' : ageLabel(d.age_days)}
                </td>
                <td>
                  {d.subscribed_tenants === 0
                    ? <span className="tag tag-neutral">No tenants</span>
                    : <StatusTag ok={!d.overdue} />}
                </td>
                <td style={{ fontSize: 13, opacity: 0.8 }}>
                  {d.subscribed_tenants > 0 && `${d.subscribed_tenants} tenant(s) · based on billed runs only`}
                </td>
              </tr>
            ))}
            <tr>
              <td>Monthly deep research</td>
              <td title={formatDate(crons.monthly_research.last_snapshot_at)}>{ageLabel(crons.monthly_research.age_days)}</td>
              <td><StatusTag ok={!crons.monthly_research.overdue} /></td>
              <td style={{ fontSize: 13, opacity: 0.8 }}>{crons.monthly_research.snapshots_last_35d} snapshot(s) in 35d</td>
            </tr>
            {crons.monthly_sentiment && (
              <tr>
                <td>Sentiment (odd months)</td>
                <td title={formatDate(crons.monthly_sentiment.last_snapshot_at)}>{ageLabel(crons.monthly_sentiment.age_days)}</td>
                <td>
                  {crons.monthly_sentiment.provider_configured
                    ? <StatusTag ok={!crons.monthly_sentiment.overdue} />
                    : <span className="tag tag-neutral">No provider key</span>}
                </td>
                <td style={{ fontSize: 13, opacity: 0.8 }}>{crons.monthly_sentiment.snapshots_last_35d} snapshot(s) in 35d</td>
              </tr>
            )}
            {crons.monthly_products && (
              <tr>
                <td>Product lineups (even months)</td>
                <td title={formatDate(crons.monthly_products.last_snapshot_at)}>{ageLabel(crons.monthly_products.age_days)}</td>
                <td>
                  {crons.monthly_products.provider_configured
                    ? <StatusTag ok={!crons.monthly_products.overdue} />
                    : <span className="tag tag-neutral">No provider key</span>}
                </td>
                <td style={{ fontSize: 13, opacity: 0.8 }}>{crons.monthly_products.snapshots_last_35d} snapshot(s) in 35d</td>
              </tr>
            )}
          </tbody>
        </table>
      </SectionCard>

      <SectionCard
        title="Data freshness"
        subtitle={`week of ${freshness.week_start} · active brands only`}
      >
        <table className="table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th style={{ textAlign: 'right' }}>Brands</th>
              <th style={{ textAlign: 'right' }}>Synced this week</th>
              <th style={{ textAlign: 'right' }}>Researched ≤35d</th>
              <th style={{ textAlign: 'right' }}>Enrichment failed</th>
            </tr>
          </thead>
          <tbody>
            {freshness.per_tenant.map((t) => (
              <tr key={t.org_id}>
                <td>{t.name}</td>
                <td style={{ textAlign: 'right' }}>{t.brands}</td>
                <td style={{ textAlign: 'right' }}>{t.synced_this_week}</td>
                <td style={{ textAlign: 'right' }}>{t.researched_last_35d}</td>
                <td style={{ textAlign: 'right', color: t.enrichment_failed > 0 ? 'var(--color-accent-700)' : undefined }}>
                  {t.enrichment_failed}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>

      {sentiment && (
        <SectionCard
          title="Consumer product sentiment"
          subtitle={sentiment.provider_configured
            ? 'retailer reviews, Reddit & press'
            : 'Perplexity key not set — analysis disabled'}
        >
          <div className="o-stat-grid">
            <StatCard kicker="Coverage" value={`${sentiment.with_score}/${sentiment.total_brands}`} note={`${sentiment.coverage_pct}%`} />
            <StatCard kicker="Analysed ≤35d" value={sentiment.analysed_last_35d} />
            <StatCard kicker="Insufficient data" value={sentiment.insufficient} />
            <StatCard
              kicker="Provider errors"
              value={sentiment.errored}
              color={sentiment.errored > 0 ? 'var(--color-accent-700)' : undefined}
            />
          </div>
        </SectionCard>
      )}

      <SectionCard title="Discovery funnel">
        <div className="o-stat-grid">
          <StatCard
            kicker="Review backlog"
            value={funnel.backlog.pending}
            color={funnel.backlog.pending > 25 ? 'var(--color-accent-700)' : undefined}
          />
          <StatCard kicker="Oldest pending" value={ageLabel(funnel.backlog.oldest_pending_age_days)} />
          {Object.entries(funnel.discovered_last_30d_by_status).map(([status, n]) => (
            <StatCard key={status} kicker={`${status} (30d)`} value={n} />
          ))}
        </div>
        {funnel.weekly_inserts.length === 0 ? (
          <p style={{ margin: 0, fontSize: 14, opacity: 0.65 }}>No discovery output in the last 8 weeks.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Week</th>
                <th style={{ textAlign: 'right' }}>Candidates</th>
                <th>By source</th>
              </tr>
            </thead>
            <tbody>
              {funnel.weekly_inserts.map((w) => (
                <tr key={w.week_start}>
                  <td>{w.week_start}</td>
                  <td style={{ textAlign: 'right' }}>{w.count}</td>
                  <td style={{ fontSize: 13, opacity: 0.8 }}>
                    {Object.entries(w.by_source).map(([s, n]) => `${s}: ${n}`).join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard
        title="Credit spend & real cost"
        subtitle="last 30 days · credits billed vs. estimated provider COGS"
      >
        <div className="o-stat-grid">
          <StatCard kicker="Last 7 days" value={`${credits.last_7d.credits} cr`} note={`$${credits.last_7d.est_usd} est. cost`} />
          <StatCard
            kicker="Prior 7 days"
            value={`$${credits.prior_7d.est_usd}`}
            note="est. cost"
            color={credits.prior_7d.est_usd > 0 && credits.last_7d.est_usd > credits.prior_7d.est_usd * 2
              ? 'var(--color-accent-700)' : undefined}
          />
        </div>
        {credits.last_30d_by_tenant.length === 0 ? (
          <p style={{ margin: 0, fontSize: 14, opacity: 0.65 }}>No credit usage recorded in the last 30 days.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th style={{ textAlign: 'right' }}>Credits</th>
                <th style={{ textAlign: 'right' }}>Est. cost</th>
                <th>By operation</th>
              </tr>
            </thead>
            <tbody>
              {credits.last_30d_by_tenant.map((t) => (
                <tr key={t.org_id}>
                  <td>{t.name}</td>
                  <td style={{ textAlign: 'right' }}>{t.credits} cr</td>
                  <td style={{ textAlign: 'right' }}>${t.est_usd}</td>
                  <td style={{ fontSize: 13, opacity: 0.8 }}>
                    {Object.entries(t.by_operation)
                      .sort(([, a], [, b]) => b.est_usd - a.est_usd)
                      .map(([op, v]) => `${op}: ${v.count}× ($${v.est_usd})`)
                      .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {credits.cost_model && (
          <table className="table">
            <thead>
              <tr>
                <th>Operation</th>
                <th style={{ textAlign: 'right' }}>Est. cost</th>
                <th>Billed per</th>
                <th style={{ textAlign: 'right' }}>Credits/unit</th>
              </tr>
            </thead>
            <tbody>
              {credits.cost_model.map((r) => (
                <tr key={r.operation}>
                  <td title={r.operation}>{r.label}</td>
                  <td style={{ textAlign: 'right' }}>${r.estimated_usd}</td>
                  <td>{r.billed_per}</td>
                  <td style={{ textAlign: 'right', color: r.credits_per_unit > 1 ? 'var(--color-accent-700)' : undefined }}>
                    {r.credits_per_unit} cr
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title="Integrations" subtitle="API keys configured in this environment">
        <div className="o-chip-row">
          {Object.entries(integrations).map(([name, ok]) => (
            <span key={name} className={ok ? 'tag tag-accent-2' : 'tag tag-accent'}>
              {ok
                ? <CheckCircle2 size={11} strokeWidth={STROKE} style={{ marginRight: 4 }} />
                : <XCircle size={11} strokeWidth={STROKE} style={{ marginRight: 4 }} />}
              {name}
            </span>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

export default HealthTab;
